import { colors } from '@kmns-internal/furikake'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { Theme } from '@mui/material/styles'
import { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { TableActionMenu, type TableActionMenuItem } from '../TableActionMenu'
import {
  BODY_CELL_HEIGHT,
  columnWidthSx,
  getCellQuestionForColumnIndex,
  rowHeadingWidthSx,
  type TableDisplayColumn,
} from '../model'
import { TableColumnHeaderName } from './TableColumnHeaderName'

type BaseProps = {
  row: TemplateNodeSchema
  rowHeadingHidden: boolean
  displayColumns: TableDisplayColumn[]
  templateNodes: TemplateNodesDict
  activeNodeId: number
}

/** 編集モード。セル選択・行メニューは編集時のみ意味を持つため、ここで必須にする。 */
type InteractiveProps = BaseProps & {
  interactive: true
  onSelectColumn: (questionId: number) => void
  onSelectRowHeading: (rowId: number) => void
  onPasteRowHeadings: (rowId: number, text: string) => void
  rowMenuItems: TableActionMenuItem[]
}

/** 読み取り専用(プレビュー)モード。操作系コールバックは受け取らない。 */
type ReadonlyProps = BaseProps & {
  interactive?: false
}

type Props = InteractiveProps | ReadonlyProps

const cellSx = (selected: boolean, clickable: boolean) => (theme: Theme) => ({
  ...columnWidthSx(theme),
  height: BODY_CELL_HEIGHT(theme),
  px: 1.5,
  py: 0.75,
  borderRight: 1,
  borderBottom: 1,
  borderColor: 'divider',
  verticalAlign: 'middle',
  cursor: clickable ? 'pointer' : 'default',
  bgcolor: selected ? colors.primary.selected : colors.white,
  ...(clickable ? { '&:hover': { bgcolor: colors.action.hover } } : {}),
})

/**
 * 固定行の1行。セルは列と同じ構造を持ち、index で対応する。
 * セルの中身は編集画面では使わないので、プレースホルダだけを出す。
 */
export const TablePreviewFixedRow = (props: Props) => {
  const { row, rowHeadingHidden, displayColumns, templateNodes, activeNodeId } =
    props
  const rowHeading = row.section?.name?.trim()
  // 見出しのセルはフォーカスを受け取り、フォーカスがあるときだけ貼り付けを見出しの一括設定として扱う。
  const rowHeadingEditable = props.interactive === true && !rowHeadingHidden

  // 列セルも分岐質問セルも、編集プレビューでは同じプレースホルダ文言を出す。
  const renderPlaceholderCell = (
    key: string,
    selected: boolean,
    selectQuestionId: number | undefined,
  ) => {
    const clickable = props.interactive === true && selectQuestionId != null
    return (
      <Box
        component="td"
        key={key}
        onClick={event => {
          event.stopPropagation()
          if (props.interactive && selectQuestionId != null) {
            props.onSelectColumn(selectQuestionId)
          }
        }}
        sx={cellSx(selected, clickable)}
      >
        <Typography variant="body2" color="text.disabled" noWrap>
          記録時に回答
        </Typography>
      </Box>
    )
  }

  return (
    <Box component="tr">
      {props.interactive && (
        <Box
          component="td"
          sx={theme => ({
            width: theme.spacing(5),
            minWidth: theme.spacing(5),
            p: 0,
            borderRight: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: colors.white,
            textAlign: 'center',
            verticalAlign: 'middle',
          })}
        >
          {props.rowMenuItems.length > 0 && (
            <TableActionMenu items={props.rowMenuItems} ariaLabel="行の操作" />
          )}
        </Box>
      )}
      <Box
        component="th"
        scope="row"
        tabIndex={rowHeadingEditable ? 0 : undefined}
        onClick={event => {
          event.stopPropagation()
          if (props.interactive && !rowHeadingHidden) {
            props.onSelectRowHeading(row.id)
          }
        }}
        onPaste={event => {
          const text = event.clipboardData.getData('text/plain')
          if (!props.interactive || rowHeadingHidden || !text) {
            return
          }
          event.preventDefault()
          props.onPasteRowHeadings(row.id, text)
        }}
        sx={theme => ({
          ...cellSx(
            !rowHeadingHidden && activeNodeId === row.id,
            rowHeadingEditable,
          )(theme),
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -2,
          },
          ...rowHeadingWidthSx(theme, rowHeadingHidden),
          textAlign: 'left',
          ...(rowHeadingHidden && {
            bgcolor: colors.action.disabledBackground,
          }),
        })}
      >
        {!rowHeadingHidden && rowHeading && (
          <TableColumnHeaderName name={rowHeading} fontWeight={400} />
        )}
      </Box>
      {displayColumns.map(column => {
        if (column.kind === 'column') {
          const cell = getCellQuestionForColumnIndex(
            row,
            column.columnIndex,
            templateNodes,
          )
          // 固定行のセルは個別に編集しないので、クリックでは列の定義側を選択する。
          const selected =
            cell?.id === activeNodeId || column.columnId === activeNodeId
          return renderPlaceholderCell(
            `${row.id}-${column.key}`,
            selected,
            column.columnId,
          )
        }

        const selected =
          column.question != null && column.question.id === activeNodeId
        return renderPlaceholderCell(
          `${row.id}-${column.key}`,
          selected,
          column.question?.id,
        )
      })}
    </Box>
  )
}
